import { processTemplates } from '../template-processor';
import { buildOtherNameVersions } from './processors';

// Map of all available sources (both functions and constants)
const SOURCES = {
  buildOtherNameVersions,
};

export const processDynamicContentField = (
  field,
  dependencyValue,
  valuesSnapshot,
  templateSources = {},
) => {
  const config = field?.runtimeConfig;

  if (!config || typeof config !== 'object') {
    return { field, changed: false };
  }

  let changed = false;
  let next = { ...field };

  // A) apply configuration override chosen by dependencyValue (fallback to "default")
  if (config.configurations && typeof config.configurations === 'object') {
    const chosen = Object.prototype.hasOwnProperty.call(config.configurations, dependencyValue)
      ? config.configurations[dependencyValue]
      : config.configurations.default;

    if (chosen && typeof chosen === 'object') {
      for (const [k, v] of Object.entries(chosen)) {
        const nv = v && typeof v === 'object' ? JSON.parse(JSON.stringify(v)) : v;
        if (next[k] !== nv) {
          next[k] = nv;
          changed = true;
        }
      }
    }
  }

  // B) populate options from a named source (called with dependencyValue)
  if (config.options && typeof config.options === 'string') {
    const optionsFn = SOURCES[config.options];
    if (typeof optionsFn === 'function') {
      const resolved = optionsFn(valuesSnapshot);

      const nv =
        resolved && typeof resolved === 'object' ? JSON.parse(JSON.stringify(resolved)) : resolved;

      // cheap deep compare for arrays/objects; primitives compare by !==
      const prev = next.options;
      const isDifferent =
        typeof nv === 'object' || typeof prev === 'object'
          ? JSON.stringify(prev) !== JSON.stringify(nv)
          : prev !== nv;

      if (isDifferent) {
        next.options = nv;
        changed = true;
      }
    }
  }

  // C) process candidate field translations
  if (config.translations && typeof config.translations === 'object') {
    // Process translation keys (like "title")
    // Merge application templateSources (company, category, etc.) with valuesSnapshot so plain
    // variables like {{company.name}} and {{category}} resolve when re-processing
    const sourcesForTemplates = { ...templateSources, ...valuesSnapshot };
    for (const fieldKey of Object.keys(config.translations)) {
      const translationKey = config.translations[fieldKey];
      if (typeof translationKey === 'string') {
        // Use the resolved template from runtime config
        const template = config.translations[fieldKey];

        // Only process if there are still unresolved template variables
        // The template should already have static parts resolved from static processing
        if (template && typeof template === 'string' && template.includes('{{')) {
          const resolvedValue = processTemplates(template, sourcesForTemplates);

          // Update the field property
          if (next[fieldKey] !== resolvedValue) {
            next[fieldKey] = resolvedValue;
            changed = true;
          }
        }
      }
    }
  }

  return { field: changed ? next : field, changed };
};

export const processDynamicContent = (
  sections,
  fieldPathIndex,
  targetCode,
  dependencyValue,
  valuesSnapshot,
  templateSources = {},
) => {
  if (!Array.isArray(sections) || !targetCode || !fieldPathIndex) {
    return { updatedSections: sections, changed: false, fieldsToClear: [] };
  }

  const locator = fieldPathIndex[targetCode] ?? null;
  if (!locator) {
    return { updatedSections: sections, changed: false, fieldsToClear: [] };
  }

  const { sectionCode, subsectionCode, groupCode, code: fieldCode } = locator;
  const atGroupLevel = groupCode === targetCode;

  let changed = false;

  const updatedSections = sections.map(sec => {
    if (sec.code !== sectionCode) return sec;

    let secChanged = false;
    const newSubs = (sec.subsections || []).map(sub => {
      if (sub.code !== subsectionCode) return sub;

      let subChanged = false;
      const newFields = (sub.fields || []).map(f => {
        // A) wrapper itself is the target
        if (atGroupLevel && f.code === targetCode) {
          const res = processDynamicContentField(
            { ...f },
            dependencyValue,
            valuesSnapshot,
            templateSources,
          );
          if (res.changed) {
            subChanged = secChanged = changed = true;
          }
          return res.changed ? res.field : f;
        }

        // B) child under the wrapper (groupCode is the wrapper code)
        // Target may be in subFields or in altIdFields[].subFields (same treatment as subfields)
        if (!atGroupLevel && groupCode && f.code === groupCode) {
          let anyChildChanged = false;

          // B1) Check subFields
          if (Array.isArray(f.subFields)) {
            const nextSubs = f.subFields.map(sf => {
              if (sf.code !== fieldCode) return sf;
              const res = processDynamicContentField(
                { ...sf },
                dependencyValue,
                valuesSnapshot,
                templateSources,
              );
              if (res.changed) anyChildChanged = true;
              return res.changed ? res.field : sf;
            });
            if (anyChildChanged) {
              subChanged = secChanged = changed = true;
              return { ...f, subFields: nextSubs };
            }
          }

          // B2) Check altIdFields (each group's subFields)
          const altIdGroups = f.altIdFields || f.alt_id_fields;
          if (Array.isArray(altIdGroups) && altIdGroups.length > 0) {
            const nextAltIdGroups = altIdGroups.map(altGroup => {
              const innerFields = altGroup.subFields || altGroup.sub_fields || [];
              if (innerFields.length === 0) return altGroup;
              const nextInner = innerFields.map(sf => {
                if (sf.code !== fieldCode) return sf;
                const res = processDynamicContentField(
                  { ...sf },
                  dependencyValue,
                  valuesSnapshot,
                  templateSources,
                );
                if (res.changed) anyChildChanged = true;
                return res.changed ? res.field : sf;
              });
              return nextInner.some((sf, i) => sf !== innerFields[i])
                ? { ...altGroup, subFields: nextInner }
                : altGroup;
            });
            if (anyChildChanged) {
              subChanged = secChanged = changed = true;
              return { ...f, altIdFields: nextAltIdGroups };
            }
          }

          return f;
        }

        // C) plain top-level leaf (no wrapper)
        if (!groupCode && f.code === targetCode) {
          const res = processDynamicContentField(
            { ...f },
            dependencyValue,
            valuesSnapshot,
            templateSources,
          );
          if (res.changed) {
            subChanged = secChanged = changed = true;
          }
          return res.changed ? res.field : f;
        }

        return f;
      });

      return subChanged ? { ...sub, fields: newFields } : sub;
    });

    return secChanged ? { ...sec, subsections: newSubs } : sec;
  });

  return { updatedSections: changed ? updatedSections : sections, changed: changed };
};
